import { useEffect, useState } from "react"
import {Check, Shield, Clock, Zap, Heart, TrendingUp, X, PenTool, HelpCircle, MousePointerClick, Printer, Play, Sparkles, Award} from 'lucide-react'
import ActivitiesGallery from "@/components/ActivitiesGallery"
import FaqAccordion from "@/components/FaqAccordion"

const TIMER_SECONDS = 20 * 60 // 20 minutes countdown

function useCountdown(seconds: number) {
  const [timeLeft, setTimeLeft] = useState(seconds)
  useEffect(() => {
    if (timeLeft <= 0) return
    const id = setInterval(() => setTimeLeft((t) => t - 1), 1000)
    return () => clearInterval(id)
  }, [timeLeft])
  const h = Math.floor(timeLeft / 3600).toString().padStart(2, "0")
  const m = Math.floor((timeLeft % 3600) / 60).toString().padStart(2, "0")
  const s = (timeLeft % 60).toString().padStart(2, "0")
  return `${h}:${m}:${s}`
}

const testimonialPrints = [
  "https://static.lumi.new/dc/dc865a649ce6b3a11b239259d7028974.webp",
  "https://static.lumi.new/af/afd564f34f46fd3fba92bcabb0322399.webp",
  "https://static.lumi.new/4c/4c23621104bc822064370cf637667048.webp",
]

const kitContents = [
  { title: "Jogos de emoções e baralhos de sentimentos", desc: "Para criar vínculo desde o primeiro encontro" },
  { title: "Atividades projetivas por demanda clínica", desc: "Organizadas por ansiedade, TDAH, luto e comportamento" },
  { title: "Dinâmicas de vínculo", desc: "Para primeiras sessões e crianças resistentes" },
  { title: "Fichas de manejo comportamental", desc: "Prontas pra imprimir e aplicar imediatamente" },
  { title: "Materiais de psicoeducação para pais", desc: "Entregue na mesma sessão, sem preparo extra" },
]

const bonuses = [
  { num: "01", title: "Fichas de Anamnese Infantil Estruturadas", desc: "Modelos prontos para a primeira sessão com os pais. Chegue preparada desde o início.", value: "R$27,00" },
  { num: "02", title: "Roteiro de Devolutiva para Pais", desc: "Um guia claro para comunicar os resultados com segurança e profissionalismo.", value: "R$19,00" },
  { num: "03", title: "Checklist de Planejamento de Sessão", desc: "Nunca mais entre sem saber o que vai fazer. Organize cada sessão em minutos.", value: "R$17,00" },
  { num: "04", title: "Guia de Manejo com Pais Difíceis", desc: "Estratégias práticas para lidar com pais resistentes ou exigentes.", value: "R$17,00" },
  { num: "05", title: "Kit de Psicoeducação Visual para Pais", desc: "Material visual pronto para entregar e explicar diagnósticos com clareza.", value: "R$17,00" },
]

const situations = [
  "A criança que chora e quer a mãe",
  "A que perdeu alguém e não sabe nomear o que sente",
  "A que os pais separaram e está no meio do conflito",
  "A que tem dificuldade de controlar as emoções e reage com intensidade",
  "A que tem diagnóstico de TDAH e precisa de suporte estruturado",
  "A que apresenta comportamentos desafiadores frequentes",
]

const benefits = [
  { icon: Zap, title: "Sessões mais produtivas", desc: "Menos tempo montando material, mais tempo focando no que importa: a criança à sua frente." },
  { icon: Shield, title: "Mais segurança para conduzir", desc: "Chegue em qualquer atendimento sabendo exatamente qual recurso aplicar. Sem improviso." },
  { icon: Heart, title: "Mais tempo para a criança", desc: "Sem a correria de preparar sessão, você foca no vínculo e na escuta de verdade." },
  { icon: TrendingUp, title: "Maior repertório clínico", desc: "Um acervo organizado por demanda que amplia suas possibilidades em cada sessão." },
]

const transformBefore = [
  { icon: Clock, text: "Horas preparando sessões" },
  { icon: PenTool, text: "Criando atividades do zero" },
  { icon: HelpCircle, text: "Improvisando atendimentos" },
  { icon: X, text: "Dúvida sobre qual recurso utilizar" },
]

const transformAfter = [
  { icon: MousePointerClick, text: "Escolhe o recurso certo" },
  { icon: Printer, text: "Imprime na hora" },
  { icon: Play, text: "Aplica com confiança" },
  { icon: Check, text: "Sessão pronta em poucos minutos" },
]

const faqs = [
  { q: "Como recebo o acesso após a compra?", a: "Assim que o pagamento for confirmado, o acesso é enviado automaticamente para o e-mail cadastrado. Você poderá acessar o material imediatamente." },
  { q: "O material é digital ou físico?", a: "O Método Lúdico Infantil é um material 100% digital. Você recebe os arquivos em PDF para acessar, baixar e imprimir quando desejar." },
  { q: "Posso imprimir quantas vezes quiser?", a: "Sim. Após a compra, o material é seu e você pode imprimir quantas vezes precisar para utilizar em seus atendimentos." },
  { q: "Preciso ter experiência em psicoterapia infantil para utilizar o material?", a: "Não. O material foi desenvolvido para auxiliar tanto profissionais experientes quanto quem está iniciando os atendimentos infantis." },
  { q: "Funciona para atendimento online?", a: "Sim. Diversas atividades podem ser utilizadas tanto em atendimentos presenciais quanto online." },
  { q: "O pagamento é seguro?", a: "Sim. Todo o pagamento é processado em ambiente criptografado, garantindo total segurança para sua compra." },
  { q: "Como funciona a garantia?", a: "Você tem 15 dias de garantia incondicional. Se dentro desse período entender que o material não atende às suas expectativas, basta solicitar o reembolso e devolveremos 100% do valor pago." },
]

// Dispara o evento de checkout no Meta Pixel (se o Pixel estiver carregado)
function trackCheckout(plano: string, valor: number) {
  const fbq = (window as unknown as { fbq?: (...args: unknown[]) => void }).fbq
  if (typeof fbq === "function") {
    fbq("track", "InitiateCheckout", {
      content_name: plano,
      currency: "BRL",
      value: valor,
    })
  }
}

export default function Home() {
  const timer = useCountdown(TIMER_SECONDS)

  return (
    <div className="min-h-screen bg-white font-sans text-gray-800">

      {/* TOP BAR */}
      <div className="bg-teal-600 text-white text-center text-sm py-2 px-4 font-medium">
        Oferta por tempo limitado — Esta página sairá do ar em:{" "}
        <span className="font-bold tracking-widest">{timer}</span>
      </div>

      {/* HEADER */}
      <header className="border-b border-gray-100 py-4 px-6 flex items-center justify-center">
        <div className="flex flex-col items-center gap-0.5">
          <span className="text-xs font-semibold tracking-widest text-teal-600 uppercase border border-teal-200 rounded-full px-4 py-1 bg-teal-50">
            Método Lúdico Infantil
          </span>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-14 pb-16 md:pt-20 md:pb-24">
        <div className="grid md:grid-cols-2 gap-12 md:gap-10 items-center">
          {/* Left: copy */}
          <div className="text-center md:text-left">
            <p className="text-gray-500 text-sm md:text-base font-medium mb-5">Para psicólogas e terapeutas infantis</p>
            <h1 className="text-4xl md:text-[3.25rem] font-extrabold leading-[1.08] text-gray-900 mb-6 tracking-tight">
              PARE DE PERDER HORAS<br />
              PREPARANDO CADA<br />
              <span className="text-teal-500">SESSÃO INFANTIL.</span>
            </h1>
            <p className="text-gray-600 text-lg mb-4 max-w-lg mx-auto md:mx-0">
              Tenha acesso a mais de 80 atividades terapêuticas organizadas por demanda, prontas para imprimir e aplicar, economizando horas de preparação sem abrir mão da qualidade do atendimento.
            </p>
            <p className="text-gray-500 text-base mb-8 max-w-md mx-auto md:mx-0">
              Você só escolhe a atividade ideal, imprime e aplica na sessão. <strong className="text-gray-700">Simples, rápido e profissional.</strong>
            </p>

            {/* Trust badges */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 mb-9 max-w-md mx-auto md:mx-0">
              {[
                "Mais de 80 atividades terapêuticas",
                "Prontas para imprimir",
                "Acesso imediato após a compra",
                "Garantia incondicional de 15 dias",
              ].map((b) => (
                <span key={b} className="flex items-center gap-2 text-sm text-gray-700">
                  <Check size={16} className="text-teal-500 flex-shrink-0" />
                  {b}
                </span>
              ))}
            </div>

            <a
              href="#pricing"
              style={{ backgroundColor: "#19C37D" }}
              className="inline-block hover:opacity-90 text-white font-bold text-lg px-10 py-4 rounded-xl shadow-lg transition-all duration-200 hover:scale-105"
            >
              QUERO ACESSAR O MÉTODO AGORA
            </a>
            <p className="text-xs text-gray-400 mt-4">
              🔒 Compra 100% segura • Acesso imediato • Garantia de 15 dias
            </p>
          </div>

          {/* Right: mockup */}
          <div className="relative flex justify-center md:justify-end">
            <div className="relative w-full max-w-md">
              <img
                src="https://static.lumi.new/ee/eefe50887f407104e8873d01029002e3.png"
                alt="Método Lúdico Infantil — capa e páginas internas do método completo"
                className="w-full rounded-2xl shadow-2xl ring-1 ring-black/5 object-cover transition-all duration-500 hover:shadow-teal-200/50"
              />
              {/* Badge com nome do produto */}
              <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-max">
                <div className="bg-white border border-teal-100 rounded-2xl px-5 py-2.5 shadow-xl flex items-center gap-2.5">
                  <div className="w-2 h-2 rounded-full bg-teal-500 animate-pulse" />
                  <span className="text-teal-700 font-extrabold text-sm tracking-wide whitespace-nowrap">Método Lúdico Infantil</span>
                  <div className="h-4 w-px bg-teal-200" />
                  <span className="text-teal-500 font-semibold text-xs whitespace-nowrap">+160 recursos</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* TRANSFORMATION */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-extrabold text-center text-gray-900 mb-2">Como o Método transforma sua rotina</h2>
          <p className="text-center text-gray-500 mb-10">De uma preparação exaustiva para uma sessão pronta em minutos.</p>
          <div className="grid md:grid-cols-2 gap-6">
            {/* ANTES */}
            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-7 transition-all duration-300 hover:shadow-md">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center">
                  <X size={16} className="text-gray-500" />
                </div>
                <h3 className="font-bold text-gray-500 uppercase text-sm tracking-widest">Antes</h3>
              </div>
              <ul className="space-y-4">
                {transformBefore.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-gray-200/70 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon size={15} className="text-gray-500" />
                    </div>
                    <span className="text-gray-600 text-sm">{text}</span>
                  </li>
                ))}
              </ul>
            </div>
            {/* DEPOIS */}
            <div className="bg-teal-50 border-2 border-teal-200 rounded-2xl p-7 transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
              <div className="flex items-center gap-2 mb-5">
                <div className="w-8 h-8 rounded-full bg-teal-500 flex items-center justify-center">
                  <Check size={16} className="text-white" />
                </div>
                <h3 className="font-bold text-teal-600 uppercase text-sm tracking-widest">Depois</h3>
              </div>
              <ul className="space-y-4">
                {transformAfter.map(({ icon: Icon, text }) => (
                  <li key={text} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg bg-teal-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Icon size={15} className="text-white" />
                    </div>
                    <span className="text-gray-700 text-sm font-medium">{text}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* KIT CONTENTS */}
      <section className="bg-gray-50 py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-3xl font-extrabold text-center text-gray-900 mb-2">O que você vai encontrar por dentro</h2>
          <p className="text-center text-gray-500 mb-10"><strong className="text-gray-700">Você não precisa criar atividades do zero.</strong> Materiais organizados por demanda clínica, prontos pra <strong className="text-gray-700">imprimir e aplicar imediatamente</strong>.<br />Cada um pensado para situações reais do seu consultório.</p>
          <div className="grid md:grid-cols-2 gap-4">
            {kitContents.map((item) => (
              <div key={item.title} className="flex gap-3 bg-white rounded-xl p-5 shadow-sm border border-gray-100">
                <div className="w-8 h-8 rounded-full bg-teal-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <Check size={16} className="text-teal-600" />
                </div>
                <div>
                  <p className="font-semibold text-gray-900 text-sm">{item.title}</p>
                  <p className="text-gray-500 text-xs mt-0.5">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SITUATIONS */}
      <section className="py-16 px-6">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-3">Feito para as sessões que mais desafiam você</h2>
          <p className="text-gray-500 mb-10">Sabe aquelas situações que travam qualquer atendimento? O Método te acompanha em cada uma delas.</p>
          <div className="grid sm:grid-cols-2 gap-3 text-left mb-10">
            {situations.map((s) => (
              <div key={s} className="flex items-start gap-3 bg-teal-50 rounded-xl px-4 py-3 border border-teal-100">
                <Check size={16} className="text-teal-500 mt-0.5 flex-shrink-0" />
                <span className="text-gray-700 text-sm">{s}</span>
              </div>
            ))}
          </div>
          <a
            href="#pricing"
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white font-bold text-base px-8 py-3.5 rounded-xl shadow-md transition-all duration-200 hover:scale-105"
          >
            Quero acessar o Método Lúdico Infantil
          </a>
          <p className="text-xs text-gray-400 mt-3">✓ Acesso imediato • ✓ Download em PDF • ✓ Garantia de 15 dias</p>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="bg-gray-50 py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-extrabold text-center text-gray-900 mb-2">O que muda no seu dia a dia</h2>
          <p className="text-center text-gray-500 mb-10">Não é sobre ter mais material. É sobre atender com mais leveza e segurança.</p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {benefits.map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100 text-center transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <div className="w-12 h-12 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                  <Icon size={22} className="text-teal-600" />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 text-sm">{title}</h3>
                <p className="text-gray-500 text-sm">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ACTIVITIES GALLERY */}
      <ActivitiesGallery />

      {/* BONUSES */}
      <section className="py-16 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <span className="inline-block bg-teal-500 text-white text-xs font-bold px-4 py-1.5 rounded-full uppercase tracking-widest mb-4">5 Bônus Exclusivos</span>
            <h2 className="text-3xl font-extrabold text-gray-900">No Método Completo, você ainda recebe</h2>
            <p className="text-gray-500 mt-2">Bônus que resolvem as maiores dores do dia a dia clínico</p>
          </div>
          <div className="flex justify-center mb-10 overflow-hidden rounded-2xl max-w-lg mx-auto">
            <img
              src="https://static.lumi.new/18/189deb6bd42d590ab2042fa99a80b34a.png"
              alt="5 bônus exclusivos do Método Lúdico Infantil"
              className="w-full rounded-2xl shadow-xl ring-1 ring-black/5 object-cover transition-transform duration-500 hover:scale-105"
            />
          </div>
          <div className="grid md:grid-cols-2 gap-5">
            {bonuses.map((b) => (
              <div key={b.num} className="relative bg-white rounded-2xl p-6 shadow-sm border border-gray-100 overflow-hidden transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
                <div className="absolute top-0 right-0 bg-teal-500 text-white text-[10px] font-bold px-3 py-1.5 rounded-bl-xl text-right leading-tight">
                  <span className="line-through opacity-70">Valor: {b.value}</span><br />
                  <span className="text-xs">Hoje incluso grátis</span>
                </div>
                <p className="text-4xl font-black text-teal-100 mb-1">{b.num}</p>
                <h3 className="font-bold text-gray-900 text-sm mb-1 pr-24">{b.title}</h3>
                <p className="text-gray-500 text-xs">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* TESTIMONIALS (PRINTS) */}
      <section className="bg-gray-50 py-16 px-6">
        <div className="max-w-5xl mx-auto text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">O que estão dizendo</h2>
          <p className="text-gray-500 mb-10">Psicólogas e mães que já usam o método — mensagens reais de quem aplicou nas sessões.</p>

          <div className="grid sm:grid-cols-3 gap-6">
            {testimonialPrints.map((src, idx) => (
              <div
                key={src}
                style={{ transform: idx === 1 ? "none" : idx === 0 ? "rotate(-1.5deg)" : "rotate(1.5deg)" }}
                className="bg-white rounded-xl shadow-lg overflow-hidden transition-all duration-300 hover:shadow-2xl hover:-translate-y-1 hover:rotate-0"
              >
                <img
                  src={src}
                  alt="Depoimento real de cliente do Método Lúdico Infantil"
                  className="w-full h-auto object-cover"
                />
              </div>
            ))}
          </div>

          <p className="text-gray-400 text-xs mt-6">Prints reais de conversas com clientes do Método Lúdico Infantil.</p>
        </div>
      </section>

      {/* PRICING */}
      <section id="pricing" className="py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-extrabold text-gray-900 mb-2">Escolha seu método</h2>
          <p className="text-gray-500 mb-10">Os dois métodos dão acesso imediato. A diferença está em o quanto você quer resolver de uma vez.</p>

          <div className="grid md:grid-cols-2 gap-6 items-stretch">
            {/* Basic */}
            <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-8 flex flex-col">
              <div className="mb-5 -mt-2 overflow-hidden rounded-xl">
                <img
                  src="https://static.lumi.new/25/25c551c504335bb495696e35a8e3a04a.png"
                  alt="Plano Básico — Método Lúdico Infantil"
                  className="w-full h-44 object-cover rounded-xl shadow-sm transition-transform duration-500 hover:scale-105"
                />
              </div>
              <h3 className="font-bold text-lg text-gray-900 mb-1">Plano Básico</h3>
              <p className="text-gray-400 text-sm mb-6">Ideal para começar</p>
              <div className="mb-6">
                <p className="text-gray-400 text-sm line-through">De R$47,90</p>
                <p className="text-4xl font-extrabold text-gray-900">R$19,90</p>
                <p className="text-gray-400 text-sm">Pagamento único</p>
              </div>
              <ul className="space-y-2 mb-8 text-sm text-left text-gray-700">
                {["+80 recursos terapêuticos lúdicos", "Organizados por demanda clínica", "Acesso imediato em PDF"].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <Check size={15} className="text-teal-500 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <a
                href="https://pay.cakto.com.br/qedwhef_978132"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCheckout("Plano Básico", 19.9)}
                className="mt-auto block border-2 border-teal-500 text-teal-600 font-bold py-3 rounded-xl hover:bg-teal-50 transition text-center"
              >
                Quero acesso imediato
              </a>
              <p className="text-xs text-gray-400 text-center mt-3">✓ Acesso imediato • ✓ Download em PDF • ✓ Pagamento Seguro • ✓ Garantia de 15 dias</p>
            </div>

            {/* Complete */}
            <div className="bg-teal-500 rounded-2xl shadow-2xl ring-4 ring-teal-500/20 p-8 flex flex-col relative overflow-hidden transition-all duration-300 hover:-translate-y-1 md:scale-[1.03]">
              <div className="absolute top-4 right-4 flex flex-col items-end gap-1.5 z-10">
                <span className="bg-white text-teal-600 text-xs font-bold px-3 py-1 rounded-full">MAIS VENDIDO</span>
                <span className="flex items-center gap-1 bg-yellow-400 text-teal-900 text-xs font-bold px-3 py-1 rounded-full shadow uppercase tracking-wide"><Award size={12} /> Melhor custo-benefício</span>
              </div>
              <div className="mb-5 -mt-2 overflow-hidden rounded-xl">
                <img
                  src="https://static.lumi.new/e4/e40d37f1633f4a0f5f605d6d4d575904.png"
                  alt="Plano Completo — Método Lúdico Infantil"
                  className="w-full h-44 object-cover rounded-xl shadow-sm transition-transform duration-500 hover:scale-105"
                />
              </div>
              <p className="text-teal-100 text-xs font-semibold uppercase tracking-widest mb-1">+160 Recursos Inclusos</p>
              <h3 className="font-bold text-lg text-white mb-1">Plano Completo</h3>
              <p className="text-teal-100 text-sm mb-6">O método completo para transformar suas sessões</p>
              <div className="mb-6">
                <p className="text-teal-200 text-sm line-through">De R$114,90</p>
                <p className="text-4xl font-extrabold text-white">R$37,90</p>
                <p className="text-teal-100 text-sm">Ou 6x de R$ 7,45 • pagamento único • acesso imediato</p>
                <span className="inline-block mt-1 bg-white/20 text-white text-xs px-3 py-0.5 rounded-full">Você economiza R$77,00</span>
              </div>
              <ul className="space-y-2 mb-8 text-sm text-left text-white">
                {[
                  "+160 recursos terapêuticos lúdicos",
                  "Organizados por demanda clínica",
                  "Acesso imediato em PDF",
                  "Fichas de Anamnese Infantil — R$27,00",
                  "Roteiro de Devolutiva para Pais — R$19,00",
                  "Checklist de Planejamento de Sessão — R$17,00",
                  "Guia de Manejo com Pais Difíceis — R$17,00",
                  "Kit de Psicoeducação Visual — R$17,00",
                ].map((f) => (
                  <li key={f} className="flex items-center gap-2">
                    <Check size={15} className="text-teal-200 flex-shrink-0" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="bg-white/10 rounded-xl p-3 mb-5 flex items-start gap-2">
                <Sparkles size={16} className="text-yellow-300 flex-shrink-0 mt-0.5" />
                <p className="text-teal-50 text-xs text-left">Por menos de R$18 a mais, você leva o dobro de recursos <strong>+ os 5 bônus</strong>. É a escolha que mais resolve de uma vez.</p>
              </div>
              <a
                href="https://pay.cakto.com.br/323qify_978141"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackCheckout("Plano Completo", 37.9)}
                className="mt-auto block bg-white text-teal-600 font-bold py-3.5 rounded-xl hover:bg-teal-50 transition text-center shadow-lg hover:scale-[1.02] duration-200"
              >
                Quero acessar o Método Lúdico Infantil
              </a>
              <p className="text-teal-100 text-xs text-center mt-3">
                ✓ Acesso imediato • ✓ Download em PDF • ✓ Pagamento Seguro • ✓ Garantia de 15 dias
              </p>
            </div>
          </div>

          {/* Payment methods */}
          <div className="flex items-center justify-center gap-6 mt-8 text-sm text-gray-400">
            <span className="flex items-center gap-1.5"><Shield size={14} className="text-teal-500" /> Cartão de crédito</span>
            <span className="flex items-center gap-1.5"><Shield size={14} className="text-teal-500" /> Pix</span>
            <span className="flex items-center gap-1.5"><Shield size={14} className="text-teal-500" /> Boleto</span>
            <span className="flex items-center gap-1.5"><Shield size={14} className="text-teal-500" /> Compra 100% segura</span>
          </div>
        </div>
      </section>

      {/* GUARANTEE */}
      <section className="bg-gray-50 py-14 px-6">
        <div className="max-w-xl mx-auto text-center">
          <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-5">
            <Shield size={28} className="text-teal-600" />
          </div>
          <h2 className="text-2xl font-extrabold text-gray-900 mb-3">Compra sem risco</h2>
          <p className="text-gray-500 mb-6">
            Você tem <strong>15 dias de garantia</strong> após a compra. Se por qualquer motivo não ficar satisfeita, devolvemos 100% do seu dinheiro. Sem perguntas.
          </p>
          <div className="inline-flex flex-col items-center gap-1 bg-white border-2 border-teal-200 rounded-2xl px-10 py-6 shadow-md">
            <span className="text-5xl font-extrabold text-teal-500 leading-none">15 dias</span>
            <span className="text-teal-700 font-semibold text-sm uppercase tracking-widest">de garantia incondicional</span>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="py-14 px-6 text-center">
        <div className="max-w-xl mx-auto">
          <span className="text-sm font-semibold uppercase tracking-widest text-teal-500 mb-3 block">Oferta válida até: {timer}</span>
          <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Garanta o seu antes que encerre</h2>
          <p className="text-gray-500 mb-7"><strong className="text-gray-700">Acesso imediato após a compra.</strong> Imprime e usa já na próxima sessão. <strong className="text-gray-700">Economize horas todas as semanas.</strong></p>
          <a
            href="#pricing"
            className="inline-block bg-teal-500 hover:bg-teal-600 text-white font-bold text-lg px-10 py-4 rounded-xl shadow-lg transition-all duration-200 hover:scale-105"
          >
            Quero acesso imediato
          </a>
          <p className="text-xs text-gray-400 mt-4">✓ Acesso imediato • ✓ Download em PDF • ✓ Pagamento Seguro • ✓ Garantia de 15 dias</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white py-16 px-6">
        <div className="max-w-2xl mx-auto">
          <h2 className="text-3xl font-extrabold text-center text-gray-900 mb-2">Ainda tem alguma dúvida?</h2>
          <p className="text-center text-gray-500 mb-10">Respondemos as perguntas mais comuns antes da compra.</p>
          <FaqAccordion items={faqs} />
        </div>
      </section>

      {/* FOOTER */}
      <footer className="border-t border-gray-100 py-8 px-6 text-center text-gray-400 text-xs">
        <p className="font-semibold text-gray-600 mb-1">Método Lúdico Infantil</p>
        <p>© {new Date().getFullYear()} Todos os direitos reservados.</p>
        <p className="mt-2">Este produto é digital. O acesso é enviado imediatamente após a confirmação do pagamento.</p>
      </footer>
    </div>
  )
}
